import { useState } from 'preact/hooks';
import { Button, Pill, timeAgo, toast } from '../ui';

interface WorkerRow {
	id: string;
	name: string;
	status: 'online' | 'idle' | 'missing';
	lastSeen: number | null;
	running: number;
	info: { version: string; provider: string; models: Record<string, string>; name: string } | null;
}

/**
 * Local workers for a campaign or story: each has its own secret, shown once when added, and can be
 * revoked on its own. Several can run at once; the server keeps work in order.
 */
export function Workers(p: {
	status: { workers?: WorkerRow[]; recent?: any[] } | null;
	campaignId: string;
	add: (name: string) => Promise<{ id: string; name: string; secret: string; ok?: boolean; error?: string }>;
	revoke: (id: string) => Promise<unknown>;
}) {
	const [name, setName] = useState('');
	const [fresh, setFresh] = useState<{ name: string; secret: string } | null>(null);
	const [busy, setBusy] = useState(false);
	const workers = p.status?.workers ?? [];
	return (
		<div>
			{workers.length ? (
				<ul class="list small">
					{workers.map((w) => (
						<li key={w.id}>
							<Pill tone={w.status === 'online' ? 'ok' : w.status === 'idle' ? 'accent' : 'muted'}>{w.status}</Pill> <strong>{w.name}</strong>
							{w.info ? ` · ${w.info.provider} · v${w.info.version}` : ' · never connected'}
							{w.lastSeen ? ` · seen ${timeAgo(w.lastSeen)}` : ''}
							{w.running ? ` · ${w.running} running` : ''}{' '}
							<button
								class="linkish"
								onClick={async () => {
									if (!confirm(`Revoke "${w.name}"? Its secret stops working immediately and its jobs go back in the queue.`)) return;
									await p.revoke(w.id);
									toast('Worker revoked.', 'ok');
								}}
							>
								Revoke
							</button>
						</li>
					))}
				</ul>
			) : (
				<p class="small muted">No workers yet. Add one, then paste its secret into the local worker.</p>
			)}
			<div class="row">
				<input value={name} onInput={(e) => setName(e.currentTarget.value)} placeholder="Name, e.g. Studio GPU" maxLength={40} aria-label="Worker name" />
				<Button
					small
					variant="ghost"
					busy={busy}
					onClick={async () => {
						setBusy(true);
						try {
							const r = await p.add(name || `worker ${workers.length + 1}`);
							if (r.ok === false) throw new Error(r.error ?? 'Could not add the worker.');
							setFresh({ name: r.name, secret: r.secret });
							setName('');
						} catch (e) {
							toast((e as Error).message, 'warn');
						} finally {
							setBusy(false);
						}
					}}
				>
					Add worker
				</Button>
			</div>
			{fresh ? (
				<div class="callout">
					<p class="small">
						Secret for <strong>{fresh.name}</strong>. Paste it into that machine's local worker now with campaign id <code>{p.campaignId}</code>. It is stored hashed and won't be shown again.
					</p>
					<code class="secret">{fresh.secret}</code>
					<Button small onClick={() => navigator.clipboard?.writeText(fresh.secret).then(() => toast('Copied.', 'ok'))}>
						Copy
					</Button>
					<Button small variant="quiet" onClick={() => setFresh(null)}>
						Done
					</Button>
				</div>
			) : null}
			<p class="small muted">Each machine uses its own secret. Work for the same area is always done one job at a time, in order; different areas can run on different workers at once.</p>
		</div>
	);
}
